// Hook para enviar un mensaje en la conversación. INSERT directo bajo
// RLS (mensajes_insert_kine / mensajes_insert_paciente).
//
// El sender lo determina el caller — viene del contexto (kine logueado
// inserta como 'kine', paciente como 'paciente'). RLS verifica el
// WITH CHECK de sender + ownership del paciente.

import { useState } from "react";
import { supabase } from "../client";
import type { MensajeRow, MensajeSender } from "./useMensajesConversacion";

export interface UseEnviarMensajeReturn {
  enviar: (params: {
    pacienteId: string;
    sender: MensajeSender;
    content: string;
  }) => Promise<MensajeRow | null>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

export function useEnviarMensaje(): UseEnviarMensajeReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar({
    pacienteId,
    sender,
    content,
  }: {
    pacienteId: string;
    sender: MensajeSender;
    content: string;
  }): Promise<MensajeRow | null> {
    const trimmed = content.trim();
    if (trimmed.length === 0) {
      setError("Escribí un mensaje");
      return null;
    }
    if (trimmed.length > 2000) {
      setError("El mensaje es muy largo (máx 2000 caracteres)");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: insertError } = await supabase
        .from("mensajes")
        .insert({
          paciente_id: pacienteId,
          sender,
          content: trimmed,
        })
        .select("id, paciente_id, sender, content, created_at, read_at")
        .single<MensajeRow>();

      if (insertError) {
        console.warn("[useEnviarMensaje] insert error", insertError);
        setError(insertError.message);
        return null;
      }
      if (!data) {
        setError("No se pudo enviar el mensaje");
        return null;
      }
      return data;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useEnviarMensaje] failed", e);
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return { enviar, loading, error, reset: () => setError(null) };
}
